import React, { useRef, useImperativeHandle } from 'react';
import { Label } from '@/components/ui/label';
import { Upload, Image as ImageIcon, AlertCircle, RefreshCw, WifiOff } from 'lucide-react';
import { useImageUpload } from '@/hooks/domains/storage/useImageUpload';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { NetworkStatus } from '@/utils/networkUtils';

interface ImageUploadProps {
  user: any;
  bucketName: string;
  onImageUploaded: (urls: string[]) => void;
  onPreview?: (previewUrls: string[]) => void;
  label?: string;
  buttonText?: string;
  previewSize?: 'small' | 'medium' | 'large';
  aspectRatio?: 'square' | 'wide' | 'portrait';
  showPreview?: boolean;
  multiple?: boolean;
  hideLocalStorageWarning?: boolean;
  onUploadStatusChange?: (uploading: boolean) => void;
}

export interface ImageUploadHandle {
  openFilePicker: () => void;
}

const ImageUpload = React.forwardRef<ImageUploadHandle, ImageUploadProps>(
  ({
    user,
    bucketName,
    onImageUploaded,
    onPreview,
    label = 'Image',
    buttonText,
    previewSize = 'medium',
    aspectRatio = 'square',
    showPreview = true,
    multiple = false,
    hideLocalStorageWarning = false,
    onUploadStatusChange
  }, ref) => {
    const { 
      uploading, 
      imagePreviews, 
      currentError,
      handleImageUpload, 
      uploadAttempts, 
      maxUploadAttempts,
    } = useImageUpload({
      user,
      onImageUrlChange: onImageUploaded,
      bucketName
    });
    
    const [networkOnline, setNetworkOnline] = React.useState(NetworkStatus.isOnline());
    const inputRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => ({
      openFilePicker: () => {
        if (inputRef.current) {
          inputRef.current.click();
        }
      }
    }));

    React.useEffect(() => {
      const handleOnline = () => setNetworkOnline(true);
      const handleOffline = () => setNetworkOnline(false);

      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);
      
      return () => {
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
      };
    }, []);

    React.useEffect(() => {
      if (onUploadStatusChange) {
        onUploadStatusChange(uploading);
      }
    }, [uploading, onUploadStatusChange]);

    const validateDisplayImage = (url?: string | null): boolean => {
      if (!url) return false;
      
      try {
        if (url.startsWith('data:image/')) {
          return true;
        }
        
        return url.startsWith('http://') || url.startsWith('https://');
      } catch (e) {
        console.warn('Invalid image URL format:', url, e);
        return false;
      }
    };

    const isValidImage = (url: string | null) => validateDisplayImage(url);
    const progressValue = uploading ? (uploadAttempts / maxUploadAttempts) * 100 : 0;
    const isDataUrl = (url: string | null) => url?.startsWith('data:image/');

    const handleClick = (e: React.MouseEvent) => {
      if (uploading || !user || !networkOnline) {
        e.preventDefault();
        return;
      }
    };

    const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
      e.currentTarget.src = '/placeholder.svg';
    };


    const getPreviewClasses = () => {
      const sizeClasses = {
        small: 'w-24 h-24',
        medium: 'w-32 h-32',
        large: 'w-48 h-48'
      };
      
      const aspectClasses = {
        square: 'aspect-square',
        wide: 'aspect-video',
        portrait: 'aspect-[3/4]'
      };
      
      return `${sizeClasses[previewSize]} ${aspectClasses[aspectRatio]}`;
    };

    return (
      <div className="space-y-2">
        {label && label.trim() !== '' && <Label htmlFor="image">{label}</Label>}
        
        {!networkOnline && (
          <Alert variant="destructive" className="py-2">
            <WifiOff className="h-4 w-4 mr-2" />
            <AlertDescription>
              You are currently offline. Images will be stored locally until your connection is restored.
            </AlertDescription>
          </Alert>
        )}
        
        
        <div className="grid gap-4">
          {showPreview && imagePreviews.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {imagePreviews.map((image, index) => (
                <div key={index} className={`overflow-hidden rounded bg-muted relative ${getPreviewClasses()}`}>
                  <img 
                    src={isValidImage(image) ? image : '/placeholder.svg'} 
                    alt={`Preview ${index + 1}`} 
                    className="w-full h-full object-cover" 
                    onError={handleImageError}
                  />
                  {!isValidImage(image) && !uploading && (
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center text-white">
                      <p className="text-sm">Invalid image URL</p>
                    </div>
                  )}
                  {uploading && (
                    <div className="absolute inset-0 bg-black/30 flex flex-col items-center justify-center text-white p-4">
                      <p className="text-sm mb-2">Uploading... Attempt {uploadAttempts}/{maxUploadAttempts}</p>
                      <Progress value={progressValue} className="w-full h-2" />
                    </div>
                  )}
                  {isDataUrl(image) && !uploading && (
                    <div className="absolute top-2 right-2 bg-yellow-500/80 text-white text-xs px-2 py-1 rounded">
                      Local Storage
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          
          {buttonText && buttonText.trim() !== '' && (
            <div className="flex items-center">
              <label 
                htmlFor="image-upload" 
                className={`flex items-center justify-center gap-2 w-full py-2 px-4 border border-input rounded-md ${!uploading && !currentError && networkOnline && 'cursor-pointer hover:bg-accent hover:text-accent-foreground'} transition-colors ${(uploading || currentError || !networkOnline || !user) && 'opacity-50'}`}
                onClick={handleClick}
              >
                {!networkOnline ? (
                  <>
                    <WifiOff className="h-4 w-4" />
                    Offline
                  </>
                ) : uploading ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    Uploading...
                  </>
                ) : imagePreviews.length > 0 ? (
                  <>
                    <ImageIcon className="h-4 w-4" />
                    Add More Images
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4" />
                    {buttonText}
                  </>
                )}
                <input
                  ref={inputRef}
                  id="image-upload"
                  type="file"
                  accept="image/*"
                  multiple={multiple}
                  className="hidden"
                  disabled={uploading || !user || !networkOnline}
                  onChange={handleImageUpload}
                />
              </label>
            </div>
          )}
          
          {(!buttonText || buttonText.trim() === '') && (
            <input
              ref={inputRef}
              id="image-upload"
              type="file"
              accept="image/*"
              multiple={multiple}
              className="hidden"
              disabled={uploading || !user || !networkOnline}
              onChange={handleImageUpload}
            />
          )}
          
          {!user && (
            <p className="text-xs text-destructive flex items-center gap-1">
              <AlertCircle className="h-3 w-3" />
              You must be logged in to upload images.
            </p>
          )}
          
          {imagePreviews.some(isDataUrl) && !hideLocalStorageWarning && (
            <p className="text-xs text-amber-600 flex items-center gap-1">
              <AlertCircle className="h-3 w-3" />
              Some images are stored locally. They may not be accessible when you return later.
            </p>
          )}
        </div>
      </div>
    );
  }
);

export default ImageUpload;
